import { useEffect, useRef, useState } from "react";
import {
  Box,
  Text,
  Badge,
  Wrap,
  WrapItem,
  Button,
  HStack,
} from "@chakra-ui/react";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import StaticMode from "@mapbox/mapbox-gl-draw-static-mode";
import { formatList, primaryName } from "../utils";

// Mapbox access token
mapboxgl.accessToken =
  "pk.eyJ1IjoibmVlZGxlLWRpZ2l0YWwiLCJhIjoiY2t2emc5MW1sNHBvMDJ1bzA5dHM4bXNwdCJ9.rRec_T_IwaL540E4k3DhLw";

const commodityColors = {
  gold: "#F59E0B",
  silver: "#9CA3AF",
  copper: "#F97316",
  zinc: "#3B82F6",
  lead: "#8B5CF6",
  nickel: "#10B981",
  lithium: "#06B6D4",
  cobalt: "#EC4899",
  iron: "#EF4444",
  uranium: "#14B8A6",
  other: "#64748b",
};

const getCommodityColor = (commodities) => {
  if (!commodities || commodities.length === 0) return "#F59E0B";
  const first = commodities[0].toLowerCase();
  return commodityColors[first] || "#F59E0B";
};

export const ChatMap = ({ searchResults, geoBounds, onPolygonChange }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const draw = useRef(null);
  const markersRef = useRef([]);
  const popupRef = useRef(null);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [polygonString, setPolygonString] = useState("");
  const isLoadingPolygonRef = useRef(false);
  useEffect(() => {
    if (
      (searchResults && searchResults.length > 0) ||
      geoBounds?.coordinates?.length > 0
    ) {
      if (draw.current && map.current) {
        if (map.current.hasControl(draw.current)) {
          map.current.removeControl(draw.current);
          draw.current = null;
        }
      }
    }
  }, [searchResults, draw.current]);
  // Initialize map
  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/dark-v10",
      center: [0, 20],
      zoom: 1.5,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), "top-right");

    // Initialize Draw control
    draw.current = new MapboxDraw({
      modes: {
        ...MapboxDraw.modes,
        static: StaticMode,
      },
      displayControlsDefault: false,
      controls: {
        polygon: true,
        trash: true,
      },
      defaultMode: "simple_select",
    });
    map.current.addControl(draw.current);

    map.current.on("load", () => {
      setMapLoaded(true);
    });

    // Handle polygon creation
    map.current.on("draw.create", (e) => {
      if (isLoadingPolygonRef.current) return;
      const feature = e.features[0];
      if (feature.geometry.type === "Polygon") {
        const coordinates = feature.geometry.coordinates[0];
        // Convert to format: "longitude,latitude|longitude,latitude|..."
        const polygonStr = coordinates
          .map((coord) => `${coord[0]},${coord[1]}`)
          .join("|");
        setPolygonString(polygonStr);
        if (onPolygonChange) {
          onPolygonChange(polygonStr);
        }
      }
    });

    // Handle polygon deletion
    map.current.on("draw.delete", () => {
      if (isLoadingPolygonRef.current) return;
      setPolygonString("");
      if (onPolygonChange) {
        onPolygonChange("");
      }
    });

    // Handle polygon update
    map.current.on("draw.update", (e) => {
      if (isLoadingPolygonRef.current) return;
      const feature = e.features[0];
      if (feature.geometry.type === "Polygon") {
        const coordinates = feature.geometry.coordinates[0];
        const polygonStr = coordinates
          .map((coord) => `${coord[0]},${coord[1]}`)
          .join("|");
        setPolygonString(polygonStr);
        if (onPolygonChange) {
          onPolygonChange(polygonStr);
        }
      }
    });

    return () => {
      if (draw.current && map.current) {
        map.current.removeControl(draw.current);
        draw.current = null;
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Render geoBounds polygon when provided
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Set flag to prevent event handlers from firing
    isLoadingPolygonRef.current = true;

    // If geoBounds is provided, add it to the map
    if (geoBounds) {
      setPolygonString("geoBounds", geoBounds.coordinates[0].join("|"));
      if (!map.current.getSource("geoBounds")) {
        map.current.addSource("geoBounds", {
          type: "geojson",
          data: geoBounds,
        });
        map.current.addLayer({
          id: "geoBounds",
          type: "fill",
          source: "geoBounds",
          paint: {
            "fill-color": "#0080ff",
            "fill-opacity": 0.1,
          },
        });
        map.current.addLayer({
          id: "geoBoundsBorder",
          type: "line",
          source: "geoBounds",
          paint: {
            "line-color": "#0080ff",
            "line-width": 2,
          },
        });
      }
    } else {
      // Clear polygon string if no geoBounds
      setPolygonString("");
    }

    // Reset flag after a short delay to allow events to settle
    setTimeout(() => {
      isLoadingPolygonRef.current = false;
    }, 200);
  }, [geoBounds, mapLoaded]);

  // Update markers when search results change
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }

    if (!searchResults || searchResults.length === 0) return;

    // Filter results with valid coordinates
    const resultsWithCoords = searchResults.filter(
      (r) =>
        r.location_coordinates &&
        r.location_coordinates.length === 2 &&
        !isNaN(r.location_coordinates[0]) &&
        !isNaN(r.location_coordinates[1])
    );

    if (resultsWithCoords.length === 0) return;

    // Add new markers
    const bounds = new mapboxgl.LngLatBounds();

    resultsWithCoords.forEach((report) => {
      const [lng, lat] = report.location_coordinates;
      const color = getCommodityColor(report.commodity);

      // Create simple marker element
      const el = document.createElement("div");
      el.style.cssText = `
        width: 12px;
        height: 12px;
        background: ${color};
        border: 2px solid white;
        border-radius: 50%;
        cursor: pointer;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      `;

      // Create popup content
      const popupContent = `
        <div style="
          font-family: 'DM Sans', sans-serif;
          padding: 8px;
          max-width: 280px;
        ">
          <div style="
            font-weight: bold;
            font-size: 14px;
            color: #1e293b;
            margin-bottom: 6px;
            line-height: 1.3;
          ">
            ${primaryName(report.project_name, "Untitled Project")}
          </div>
          <div style="
            font-size: 12px;
            color: #64748b;
            margin-bottom: 8px;
          ">
            ${formatList(report.company_name, " | ")}
          </div>
          <div style="
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
            margin-bottom: 8px;
          ">
            ${(report.commodity || [])
              .map(
                (c) => `
              <span style="
                background: ${commodityColors[c.toLowerCase()] || "#64748b"};
                color: white;
                padding: 2px 8px;
                border-radius: 12px;
                font-size: 11px;
                font-weight: 500;
                text-transform: capitalize;
              ">${c}</span>
            `
              )
              .join("")}
          </div>
          <div style="
            font-size: 11px;
            color: #94a3b8;
          ">
            ${report.project_location || "Location not specified"}
          </div>
          <div style="
            font-size: 10px;
            color: #cbd5e1;
            margin-top: 4px;
          ">
            ${lat.toFixed(4)}°, ${lng.toFixed(4)}°
          </div>
        </div>
      `;

      const popup = new mapboxgl.Popup({
        offset: 15,
        closeButton: false,
        maxWidth: "300px",
      }).setHTML(popupContent);

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([lng, lat])
        .setPopup(popup)
        .addTo(map.current);

      markersRef.current.push(marker);
      bounds.extend([lng, lat]);
    });

    // Fit map to bounds if we have markers
    if (resultsWithCoords.length > 0) {
      if (resultsWithCoords.length === 1) {
        map.current.flyTo({
          center: resultsWithCoords[0].location_coordinates,
          zoom: 6,
          duration: 1500,
        });
      } else {
        map.current.fitBounds(bounds, {
          padding: { top: 50, bottom: 50, left: 50, right: 50 },
          maxZoom: 8,
          duration: 1500,
        });
      }
    }
  }, [searchResults, mapLoaded]);

  // Clear drawn polygon when polygonString is cleared externally
  useEffect(() => {
    if (!map.current || !draw.current || !mapLoaded) return;

    if (!polygonString && draw.current.getAll().features.length > 0) {
      draw.current.deleteAll();
      setIsDrawing(false);
      draw.current.changeMode("simple_select");
    }
  }, [polygonString, mapLoaded]);

  const resultsWithCoords =
    searchResults?.filter(
      (r) =>
        r.location_coordinates &&
        r.location_coordinates.length === 2 &&
        !isNaN(r.location_coordinates[0]) &&
        !isNaN(r.location_coordinates[1])
    ) || [];

  return (
    <Box
      position="relative"
      h="100%"
      w="100%"
      bg="slate.900"
      borderRadius="xl"
      overflow="hidden"
      shadow="xl"
    >
      {/* Map container */}
      <Box
        ref={mapContainer}
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
      />

      {/* Legend overlay */}
      <Box
        position="absolute"
        bottom={4}
        left={4}
        bg="rgba(15, 23, 42, 0.9)"
        backdropFilter="blur(8px)"
        borderRadius="lg"
        p={3}
        maxW="800px"
      >
        <Text fontSize="xs" color="slate.400" fontWeight="semibold" mb={2}>
          COMMODITIES
        </Text>
        <Wrap spacing={1}>
          {Object.entries(commodityColors).map(([name, color]) => (
            <WrapItem key={name}>
              <Badge
                bg={color}
                color="white"
                borderRadius="full"
                fontSize="10px"
                textTransform="capitalize"
                px={2}
                py={1}
              >
                {name}
              </Badge>
            </WrapItem>
          ))}
        </Wrap>
      </Box>

      {/* Stats overlay */}
      {searchResults && searchResults.length > 0 && (
        <Box
          position="absolute"
          top={4}
          left={4}
          bg="rgba(15, 23, 42, 0.9)"
          backdropFilter="blur(8px)"
          borderRadius="lg"
          p={3}
        >
          <Text fontSize="xs" color="slate.400" fontWeight="semibold">
            SHOWING ON MAP
          </Text>
          <Text fontSize="xl" color="amber.400" fontWeight="bold">
            {resultsWithCoords.length}
          </Text>
          <Text fontSize="xs" color="slate.500">
            of {searchResults.length} results
          </Text>
        </Box>
      )}

      {/* Drawing controls */}
      <Box
        position="absolute"
        top={4}
        right={4}
        bg="rgba(15, 23, 42, 0.9)"
        backdropFilter="blur(8px)"
        borderRadius="lg"
        p={2}
      >
        {polygonString && (
          <Text fontSize="xs" color="slate.400" mt={2} px={2}>
            Polygon active
          </Text>
        )}
      </Box>

      {/* No results message */}
      {searchResults &&
        searchResults.length > 0 &&
        resultsWithCoords.length === 0 && (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            bg="rgba(15, 23, 42, 0.95)"
            backdropFilter="blur(8px)"
            borderRadius="xl"
            p={6}
            textAlign="center"
          >
            <Text fontSize="2xl" mb={2}>
              📍
            </Text>
            <Text color="slate.300" fontWeight="medium">
              No coordinates available
            </Text>
            <Text color="slate.500" fontSize="sm">
              Results don't have location data
            </Text>
          </Box>
        )}
    </Box>
  );
};
